<template>
  <div class="cover-image-delete-confirm-mask" @click.self="onClickCancel">
    <div
      ref="dialogRoot"
      class="cover-image-delete-confirm-dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <header class="cover-image-delete-confirm-dialog__header">
        <div class="cover-image-delete-confirm-dialog__header-spacer" aria-hidden="true" />
        <h2 :id="titleId" class="cover-image-delete-confirm-dialog__title">{{ title }}</h2>
        <button
          type="button"
          class="cover-image-delete-confirm-dialog__close"
          :aria-label="String($t('common.close'))"
          @click="onClickCancel"
        >
          <IconClose />
        </button>
      </header>

      <p class="cover-image-delete-confirm-dialog__message">{{ content }}</p>

      <button type="button" class="cover-image-delete-confirm-danger-button" @click="$close(true)">
        {{ okButton || $t("common.yes") }}
      </button>
      <button type="button" class="cover-image-delete-confirm-cancel-button" @click="onClickCancel">
        {{ cancelButton || $t("common.cancel") }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import IconClose from "~icons/figma/Close";

export default Vue.extend({
  components: { IconClose },
  props: {
    title: String,
    content: String,
    okButton: {
      type: String,
      default: undefined,
    },
    cancelButton: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      titleId: "cover-image-delete-confirm-title",
    };
  },
  mounted() {
    this.$nextTick(() => {
      const root = this.$refs.dialogRoot as HTMLElement | undefined;
      root?.focus();
    });
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      this.onClickCancel();
    },
    onClickCancel() {
      this.$close(false);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.cover-image-delete-confirm-mask {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.cover-image-delete-confirm-dialog {
  width: 100%;
  max-width: 320px;
  margin: 0 16px;
  padding: 20px 20px 24px;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
}

.cover-image-delete-confirm-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.cover-image-delete-confirm-dialog__header-spacer {
  width: 28px;
  flex-shrink: 0;
}

.cover-image-delete-confirm-dialog__title {
  flex: 1;
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  text-align: center;
}

.cover-image-delete-confirm-dialog__close {
  position: relative;
  @include hover-overlay;
  display: flex;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-normal);
  cursor: pointer;

  ::v-deep svg {
    width: 14px;
    height: 14px;
  }
}

.cover-image-delete-confirm-dialog__message {
  margin: 0 0 32px;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.cover-image-delete-confirm-danger-button {
  position: relative;
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-warning);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  @include hover-overlay;
}

.cover-image-delete-confirm-cancel-button {
  position: relative;
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  @include hover-overlay;
}
</style>
